import React from 'react';
import {
  Settings,
  Mail,
  Calendar,
  Sparkles,
  Shield,
  Key,
  FileSignature,
  ExternalLink,
  BookOpen,
  Check,
} from 'lucide-react';
import SectionHeader from '../ui/SectionHeader';

const CorporateToolsPage = () => {
  return (
    <div className="animate-stagger">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Herramientas Corporativas</h1>
        <p className="text-text-secondary">
          Aplicaciones de productividad y gestión empresarial
        </p>
      </div>

      {/* Google Workspace Section */}
      <section className="section">
        <SectionHeader title="Google Workspace" />
        
        {/* Main Workspace Card */}
        <div id="google-workspace" className="card overflow-hidden mb-6 scroll-mt-24">
          <div className="bg-gradient-to-r from-blue-600 via-red-500 to-yellow-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Settings size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Plataforma Google Workspace</h2>
                <p className="text-white/80">Administración general de la organización</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-text-secondary mb-6 leading-relaxed">
              Plataforma de administración general de toda la organización con control completo 
              sobre usuarios, seguridad y recursos.
            </p>

            <h4 className="font-semibold text-text-primary mb-3">Funcionalidades de administración:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {[
                'Controlar, crear y eliminar usuarios',
                'Control de ataques de ciberseguridad',
                'Administración de almacenamiento Google Drive',
                'Crear roles y permisos de usuarios',
                'Controlar accesos a funcionalidades',
                'Gestión de dispositivos',
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-text-secondary">
                  <Check size={16} className="text-success flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://admin.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Abrir Workspace
                <ExternalLink size={16} />
              </a>
              <a
                href="https://drive.google.com/file/d/1gRKOYHc0XCdKz_Hceq3Iad8utsPDSAuZ/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <BookOpen size={16} />
                Funciones Workspace
              </a>
            </div>
          </div>
        </div>

        {/* Workspace Apps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Gmail */}
          <div id="gmail" className="card p-6 scroll-mt-24">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
              <Mail size={24} className="text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">Gmail</h3>
            <p className="text-text-secondary text-sm mb-4">
              La plataforma Gmail dispone de funcionalidades avanzadas que contribuyen a la 
              optimización y organización estratégica del correo electrónico.
            </p>
            <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-border-light">
              <a
                href="https://mail.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-sm"
              >
                Abrir Gmail
              </a>
              <a
                href="https://drive.google.com/file/d/1jp3ecLfQvu32SOmeHCGqqr9cuE3GGCY0/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm hover:underline flex items-center gap-1"
              >
                <BookOpen size={14} />
                Manual
              </a>
            </div>
          </div>

          {/* Google Calendar */}
          <div id="google-calendar" className="card p-6 scroll-mt-24">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
              <Calendar size={24} className="text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">Google Calendar</h3>
            <p className="text-text-secondary text-sm mb-4">
              Con el objetivo de optimizar la gestión del tiempo y los recursos de nuestros 
              colaboradores y clientes, hemos elaborado este manual práctico.
            </p>
            <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-border-light">
              <a
                href="https://calendar.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-sm"
              >
                Abrir Calendar
              </a>
              <a
                href="https://drive.google.com/file/d/1f873kqq0PaHuKst7451_DAEpgDsymBJM/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm hover:underline flex items-center gap-1"
              >
                <BookOpen size={14} />
                Manual Citas
              </a>
            </div>
          </div>

          {/* Gemini IA */}
          <div id="gemini" className="card p-6 scroll-mt-24">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
              <Sparkles size={24} className="text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">Gemini IA</h3>
            <p className="text-text-secondary text-sm mb-4">
              Funcionalidades que ayudan a mejorar las actividades diarias con inteligencia artificial.
            </p>
            <ul className="text-xs text-text-tertiary space-y-1 mb-4">
              <li>• Transcribe los Meet</li>
              <li>• Traduce texto en diferentes idiomas</li>
              <li>• Mejora la escritura de emails</li>
              <li>• Resume emails y documentos</li>
            </ul>
            <div className="flex flex-wrap gap-2 pt-4 border-t border-border-light">
              <a
                href="https://gemini.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-sm"
              >
                Abrir Gemini
              </a>
              <a
                href="https://drive.google.com/file/d/1h7Gw2kePKpfgWusdMeVInyTQ8nZNDSA4/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm hover:underline flex items-center gap-1"
              >
                <BookOpen size={14} />
                Manual
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other Tools Section */}
      <section className="section">
        <SectionHeader title="Otras Herramientas" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Firma Corporativa */}
          <div id="firma-corporativa" className="card overflow-hidden border-2 border-primary/20 scroll-mt-24">
            <div className="bg-primary-lighter/30 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                  <FileSignature size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">Firma Corporativa</h3>
                  <p className="text-xs text-text-secondary">Generador de firma de email</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-text-secondary text-sm mb-4">
                Sonneil SL es una empresa reconocida por sus avances tecnológicos en el sector 
                inmobiliario y su firma corporativa es esencial para profesionalizar la comunicación 
                y reforzar la marca.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://developments.sonneil.com/misc/signature-generator-v3.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-sm"
                >
                  Crear Firma
                </a>
                <a
                  href="https://drive.google.com/file/d/1mEWT1UUwvU1RRVjPlG9gbHRMZmUti9dY/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm hover:underline flex items-center gap-1"
                >
                  <BookOpen size={14} />
                  Manual
                </a>
              </div>
            </div>
          </div>

          {/* Gestor de Contraseñas */}
          <div id="gestor-contrasenas" className="card p-6 scroll-mt-24">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Key size={24} className="text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Gestor de Contraseñas</h3>
                <span className="badge-info">Seguridad</span>
              </div>
            </div>
            <p className="text-text-secondary text-sm mb-4">
              Acceso seguro a credenciales compartidas del equipo. Gestiona contraseñas de 
              forma segura y centralizada.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://sonneil.passwd.team"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-sm"
              >
                <Shield size={16} />
                Acceder
              </a>
              <a
                href="https://drive.google.com/file/d/19Z-dYj99i4YezaT43xmPX3e7mUByV-14/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm hover:underline flex items-center gap-1"
              >
                <BookOpen size={14} />
                Manual
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateToolsPage;

